<template>
    <div class="modal_cont store_find_modal">
        <div class="modal_header">
            {{ t.MainTitle }}
            <button type="button" class="btn_close" aria-label="닫기" @click="closeModal"></button>
        </div>

        <div class="modal_content">
            <div class="store_search_popup">
                <div class="intro_box">
                    <p class="intro_text" v-html="t.IntroText"></p>
                </div>

                <form class="filter_search_wrap" @submit.prevent="handleSearch">
                    <div class="input_group">
                        <SelectBox
                            :options="sidoOptions"
                            v-model="searchCondition.sido"
                            init-msg="지역 선택"
                            class="base_select"
                            @change="selectSidoComboChange"
                        />
                        <SelectBox
                            :key="`gungu-${searchCondition.sido || 'reset'}`"
                            :options="gunguOptions"
                            v-model="searchCondition.gungu"
                            init-msg="시군구 선택"
                            class="base_select"
                        />
                        <div class="base_input_wrap">
                            <Inputs
                                type="text"
                                :placeholder="t.SearchPlaceholder"
                                v-model="searchCondition.shopName"
                                class="base_input"
                            />
                        </div>
                    </div>
                    <div class="btn_group">
                        <button type="submit" class="btn_big btn_icon_search primary" :disabled="loading">
                            {{ t.BtnSearch }}
                        </button>
                        <!-- <button type="button" class="btn_big gray" :disabled="loading" @click="handleReset">
                            {{ t.BtnReset }}
                        </button> -->
                    </div>
                </form>

                <div class="store_result_header">
                    <strong>검색 결과 {{ stores.length }}건</strong>
                    <span v-if="mapLimited">지도 표시 {{ mapAddresses.length }}건</span>
                </div>

                <p v-if="loading" class="store_message">매장 정보를 불러오는 중입니다.</p>
                <p v-else-if="errorMessage" class="store_message error">{{ errorMessage }}</p>
                <p v-else-if="!stores.length" class="store_message">검색 결과가 없습니다.</p>

                <div class="store_map_mobile">
                    <div class="map_box">
                        <KakaoMap
                            :key="`mobile-${mapKey}`"
                            :center="center"
                            :level="4"
                            :height="'100%'"
                            :marker="false"
                            :addresses="selectedStore ? selectedMapAddresses : mapAddresses"
                            @error="handleMapError"
                        />
                    </div>
                </div>

                <div v-if="stores.length" class="store_layout_flex">
                    <div class="store_list_side">
                        <div
                            v-for="(store, idx) in stores"
                            :key="store.shopCode || store.shopName || idx"
                            class="store_item"
                            :class="{ active: selectedStore && selectedStoreKey(selectedStore) === selectedStoreKey(store) }"
                        >
                            <div class="item_info">
                                <div class="item_header">
                                    <strong class="store_name" @click="selectStore(store)">{{ store.shopName || "-" }}</strong>
                                </div>
                                <dl class="info_details">
                                    <template v-for="field in visibleDetailFields(store)" :key="field.key">
                                        <dt>{{ field.label }}</dt>
                                        <dd>{{ formatValue(store[field.key]) }}</dd>
                                    </template>
                                    <dt>{{ t.LabelAddress }}</dt>
                                    <dd>{{ store.address || "주소 정보 없음" }}</dd>
                                </dl>
                                <a
                                    v-if="leafletUrl(store)"
                                    :href="leafletUrl(store)"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    class="btn_leaflet"
                                >
                                    {{ t.BtnLeaflet }}
                                </a>
                            </div>
                        </div>
                    </div>

                    <div class="store_map_pc">
                        <div class="map_box_sticky">
                            <KakaoMap
                                :key="`pc-${mapKey}`"
                                :center="center"
                                :level="4"
                                :height="'100%'"
                                :marker="false"
                                :addresses="mapAddresses"
                                @error="handleMapError"
                            />
                            <button
                                v-if="selectedStore"
                                type="button"
                                class="btn_map_close"
                                @click="clearSelectedStore"
                            >
                                지도 전체보기
                            </button>
                        </div>
                    </div>
                </div>

                <div v-if="stores.length" class="pagination_area">
                    <Pagination
                        v-model="page"
                        :totalPages="1"
                        :visiblePages="5"
                        :showEllipsis="false"
                        :lang="currentLang"
                        @change="handlePage"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import KakaoMap from "@/components/KakaoMap.vue";
import Inputs from "@/components/Inputs.vue";
import Pagination from "@/components/Pagination.vue";
import SelectBox from "@/components/SelectBox.vue";
import { brandAPI } from "@/api/brandAPI.js";
import { gisAPI } from "@/api/gisAPI.js";
import modal from "@/utils/popupModal";
import { COMM_CD_GROUP_IDS, loadCommCdGroupItems } from "@/utils/CommonCode.js";

const DEFAULT_CENTER = {
    lat: 37.566826,
    lng: 126.9786567
};

const DETAIL_FIELDS = [
    { key: "telNo", label: "전화" },
    { key: "phone", label: "전화" },
    // { key: "openDt", label: "개점일" },
    // { key: "closeDt", label: "폐점일" },
    // { key: "storeType", label: "매장유형" }
];

export default {
    name: "gsrbr0203",
    components: {
        KakaoMap,
        SelectBox,
        Inputs,
        Pagination
    },
    data() {
        return {
            center: DEFAULT_CENTER,
            stores: [],
            loading: false,
            errorMessage: "",
            selectedStore: null,
            mapKey: 0,
            page: 1,
            currentLang: "ko",
            sidoOptions: [],
            gunguList: [],
            detailFields: DETAIL_FIELDS,
            searchCondition: this.createSearchCondition(),
            langData: {
                ko: {
                    MainTitle: "GS THE FRESH 매장찾기",
                    IntroText: "빠르고 편리하게 <br/>우리동네 GS THE FRESH(GS더프레시)를 찾아보세요.",
                    SearchPlaceholder: "찾으려는 동명, 도로명 또는 매장명을 입력하세요",
                    BtnSearch: "검색",
                    BtnReset: "새로고침",
                    BtnLeaflet: "행사 전단보기",
                    LabelAddress: "주소"
                },
                en: {
                    MainTitle: "Find a GS THE FRESH Store",
                    IntroText: "Find your nearest GS THE FRESH (GS Supermarket) quickly and easily!",
                    CityOptions: [{ value: "Seoul", label: "Seoul" }, { value: "Gyeonggi Province", label: "Gyeonggi Province" }],
                    DistrictOptions: [{ value: "", label: "Select City/County" }],
                    SearchPlaceholder: "Enter a neighborhood, street, or store name",
                    BtnSearch: "Search", BtnReset: "Refresh", BtnLeaflet: "View Promotional Flyer",
                    LabelTime: "Business hours"/* 260604 번역 */, LabelOffDay: "Closed days"/* 260604 번역 */, LabelAddress: "Address", LabelTel: "Phone",
                    storeData: [
                        { name: "Gagyeong iPark Branch", time: "10:00 ~ 23:00", offDay: "Closed: February 11 and February 25", address: "Address: 60 Seohyeon-ro, Heungdeok-gu, Cheongju-si, Chungcheongbuk-do, 1F (Gagyeong-dong 2270)", tel: "043-231-8012", mapImg: require("@/assets/images/dummy/gsrbr0203_map.png") },
                        { name: "Gajeong Rouen City Branch", time: "10:00 ~ 23:00", offDay: "Closed: February 8 and February 22", address: "Address: 66, Bongojae 3-ro, Seo-gu, Incheon, Rouen City Pradium Building 2, Unit 106 (Gajeong-dong 610-1)", tel: "032-569-6227", mapImg: require("@/assets/images/dummy/gsrbr0203_map.png") }
                    ]
                }
            }
        };
    },
    computed: {
        t() {
            return this.langData.ko;
        },
        gunguOptions() {
            return this.gunguList.map((gungu) => ({
                value: gungu.gunguCd,
                label: gungu.gunguNm
            }));
        },
        markerStores() {
            if (this.selectedStore) {
                return [this.selectedStore];
            }

            return Array.isArray(this.stores) ? this.stores.slice(0, 50) : [];
        },
        mapAddresses() {
            return this.markerStores
                .filter((store) => store.address)
                .map((store) => ({
                    title: store.shopName,
                    address: store.address
                }));
        },
        selectedMapAddresses() {
            if (!this.selectedStore) return [];

            return [
                {
                    title: this.selectedStore.shopName,
                    address: this.selectedStore.address
                }
            ].filter((store) => store.address);
        },
        mapLimited() {
            return !this.selectedStore && this.stores.length > this.mapAddresses.length;
        }
    },
    mounted() {
        this.loadSidoOptions();
    },
    methods: {
        async loadSidoOptions() {
            const codeItems = await loadCommCdGroupItems({
                sidoItems: COMM_CD_GROUP_IDS.GS25_BRFS_REGN_DV_CD
            });

            this.sidoOptions = codeItems.sidoItems.map((item) => ({
                value: item.value,
                label: item.title
            }));

            this.searchCondition.sido = this.sidoOptions[0]?.value || "";
            await this.selectSidoComboChange();

            if (this.searchCondition.sido && this.searchCondition.gungu) {
                await this.handleSearch();
            }
        },
        createSearchCondition() {
            return {
                shopCode: "",
                sido: "",
                gungu: "",
                shopName: "",
            };
        },
        closeModal(event) {
            modal.close(event.currentTarget);
        },
        async selectSidoComboChange() {
            this.searchCondition.gungu = "";
            this.gunguList = [];

            if (!this.searchCondition.sido) return;

            try {
                const response = await gisAPI.searchGungu({ sido: this.searchCondition.sido });
                if (response.data.resultCode === "00000") {
                    this.gunguList = response.data.result || [];
                    this.searchCondition.gungu = this.gunguOptions[0]?.value || "";
                }
            } catch (error) {
                console.error("시군구 목록 조회 오류:", error);
            }
        },
        async handleSearch() {
            this.loading = true;
            this.errorMessage = "";
            this.selectedStore = null;

            try {
                const response = await brandAPI.selectGtfStores({ ...this.searchCondition });
                this.stores = this.normalizeStores(response);
                this.mapKey += 1;
            } catch (error) {
                this.stores = [];
                this.errorMessage = "매장 정보를 불러오지 못했습니다.";
                console.error(error);
            } finally {
                this.loading = false;
            }
        },
        handleReset() {
            this.searchCondition = this.createSearchCondition();
            this.gunguList = [];
            // this.handleSearch();
        },
        handlePage(page) {
            this.page = page;
        },
        selectStore(store) {
            this.selectedStore = store;
            this.mapKey += 1;
        },
        clearSelectedStore() {
            this.selectedStore = null;
            this.mapKey += 1;
        },
        selectedStoreKey(store) {
            return store?.shopCode || store?.shopName || store?.address;
        },
        normalizeStores(response) {
            const data = response?.data;

            if (Array.isArray(data)) return data;
            if (Array.isArray(data?.data)) return data.data;
            if (Array.isArray(data?.result)) return data.result;
            if (Array.isArray(data?.list)) return data.list;
            if (Array.isArray(data?.items)) return data.items;
            if (Array.isArray(data?.data?.list)) return data.data.list;
            if (Array.isArray(data?.data?.result)) return data.data.result;

            return [];
        },
        visibleDetailFields(store) {
            return this.detailFields.filter((field) => {
                const value = store[field.key];
                return value !== null && value !== undefined && value !== "";
            });
        },
        formatValue(value) {
            return value === true ? "가능" : value;
        },
        leafletUrl(store) {
            return store.strLefltUrl || store.hmpgLefltPcUrl || store.hmpgLefltMobileUrl || store.smUrl || "";
        },
        handleMapError(error) {
            this.errorMessage = error.message;
        }
    }
};
</script>

<style scoped>
.store_find_modal.modal_cont { width: 844px; max-width: 100%; }
.modal_header { font-size: 40px; font-weight: 700; border-bottom: 0; }
.store_search_popup { padding: 10px 0; }
.intro_box { margin-bottom: 32px; }
.intro_text { font-size: 24px; font-weight: 700; line-height: 1.4; color: #161616; }
.filter_search_wrap {position:relative; display: flex; align-items: center; gap: 8px; margin-bottom: 24px; }
.filter_search_wrap .btn_icon_search {display:flex; align-items:center;}
.filter_search_wrap .btn_icon_search:before {width:16px; height:16px; margin-right:10px; background:url('@/assets/images/common/icon_set_16.png') -496px -14px; filter: brightness(0) invert(1); content:''; display:block;}
.input_group { display: flex; flex: 1; gap: 8px; }
.base_select { width: 140px; }
/* 🔥 input이 포함된 wrap이 나머지 가로 전체를 차지하게 설정 */
.base_input_wrap { flex: 1; }
/* 🔥 컴포넌트 내부의 input 태그 너비를 100%로 강제 */
.base_input_wrap :deep(input) { width: 100% !important; }
.store_result_header { display: flex; justify-content: space-between; gap: 16px; margin-bottom: 12px; font-size: 16px; color: #161616; }
.store_result_header span { color: #67676f; font-size: 14px; }
.store_message { margin: 24px 0; color: #67676f; font-size: 16px; text-align: center; }
.store_message.error { color: #d32f2f; }
.store_layout_flex { display: flex; align-items: flex-start; gap: 32px; border-top: 1px solid #161618; border-bottom:1px solid #e5e5e9;}
.store_list_side { flex: 1; width: 100%; max-height: 600px; overflow-y: auto; }
.store_item { padding: 24px 0; }
.store_item + .store_item {border-top: 1px solid #e5e5e9; }
.store_item.active { background-color: #f8f9fb; }
.store_name { font-size: 24px; font-weight: 700; display: block; margin-bottom: 12px; cursor: pointer; display:flex; align-items:center;}
/* 26.06.10 Del 이종환 .store_name::after { content: ''; width: 20px; height: 20px; margin-left: 8px; background:url('@/assets/images/common/icon_set_20.png') -324px -25px no-repeat; display: inline-block; vertical-align: middle; }*/
.info_details dt { color: #67676f; }
.info_details dd { color: #161616; }
.btn_leaflet { display: inline-flex; width: fit-content; margin-top: 24px; padding: 6px 12px; border: 1px solid #67676f; border-radius: 4px; font-size: 13px; background: #fff; color: #161616; text-decoration: none; }
.store_map_pc { width: 450px; position: sticky; top: 0; padding: 24px 0; }
.map_box_sticky { width: 100%; height: 450px; border: 1px solid #e5e5e9; border-radius: 12px; overflow: hidden; position: relative; }
.map_box_sticky :deep(.kakao-map) { height: 100%; border: none; border-radius: 12px; }
.btn_map_close { position: absolute; top: 12px; right: 12px; background: rgba(0,0,0,0.6); color: #fff; padding: 4px 8px; border-radius: 4px; font-size: 12px; }
.store_map_mobile { display: none; }
.pagination_area { margin-top: 32px; display: flex; justify-content: center; }
.btn_group { padding: 0; display: flex; gap: 8px; }

@media screen and (max-width: 1024px) {
    .store_map_pc { display: none; }
    .store_map_mobile { display: block; width: 100%; margin-top: 20px; }
    .store_map_mobile .map_box { width: 100%; height: 250px; border-radius: 12px; overflow: hidden; border: 1px solid #e5e5e9; }
    .store_map_mobile .map_box :deep(.kakao-map) { height: 100%; border: none; border-radius: 12px; }

    .input_group { width: 100%; flex-wrap: wrap; }

    .store_item.active {background-color:#fff;}
}

@media screen and (max-width:768px) {
    .store_find_modal.modal_cont { width: 100%; }
    .store_find_modal .modal_header {
        padding: 17.5px 20px;
        border-bottom: 1px solid #c4c4d0;
        font-size: 1.8rem;
        line-height: 1.4;
        letter-spacing: 0;
    }
    .store_find_modal .modal_header .btn_close {
        width: 24px;
        height: 24px;
        top: 50%;
        right: 20px;
        transform: translateY(-50%);
    }
    .store_find_modal .modal_content { margin-top: -40px; padding-top: 340px; }

    .intro_box {display:none;}

    .base_select {flex:1;}
    .base_input_wrap {width:100%; flex:1 auto;}

    .filter_search_wrap { flex-direction: column; align-items: flex-start; gap: 20px; }

    .filter_search_wrap :deep(.input input) {height:42px; padding-right:60px;}
    .filter_search_wrap :deep(.input_wrap label.input i) {font-size:1.4rem; top:21px;}
    .filter_search_wrap :deep(label.select select) {height:42px; font-size:1.4rem; line-height:1.3;}
    .filter_search_wrap :deep(label.select > div:after) {width:8px; height:8px; margin-top:-3px;}

    .filter_search_wrap .btn_group {height:auto; position:absolute; bottom:12px; right:16px; z-index:1; gap:10px;}
    .filter_search_wrap .btn_group .btn_big {width:16px; height:16px; padding:0; font-size:0; background-color:#fff; border:0; border-radius:0;}
    .filter_search_wrap .btn_group .btn_big:before {width:16px; height:16px; background-image:url('@/assets/images/common/icon_set_16.png'); content:''; filter:initial; display:block;}
    .filter_search_wrap .btn_group .btn_icon_search:before {margin-right:0; background-position:-496px -14px;}
    .filter_search_wrap .btn_group .btn_big.gray:before {background-position:-896px -14px;}

    .store_map_mobile {height:320px; margin-top:0; padding-top:0; background-color:gray; position:fixed; top:61.2px; right:0; left:0; z-index:2;}
    .store_map_mobile .map_box {height:100%; border:0; border-radius:0;}
    .store_map_mobile .map_box :deep(.kakao-map) {height:100%; border:0; border-radius:0;}

}
</style>
